/**
 * Players in a kill race over its own length, as one small line.
 *
 * It sits in the gap the row already had between the name and the figures --
 * space that was carrying nothing, and the one thing a glance at this list
 * cannot otherwise answer: not how many are in, but whether that number is
 * climbing or falling away.
 *
 * Inline SVG rather than a chart library. This is one polyline with no axes,
 * no legend and no interaction, and pulling a charting dependency into the
 * bundle for it would cost more than the feature.
 */

/** How wide a bucket should be, given how long the event runs.
 *
 *  A three-hour race bucketed hourly is three points, which is not a shape. A
 *  two-day race bucketed by the minute is three thousand, which is noise. The
 *  aim is roughly 20-40 points whatever the length, so the line reads the same
 *  on a sprint and on a weekend.
 */
export const bucketMinutes = (minutes: number): number => {
    if (minutes <= 60) return 2
    if (minutes <= 240) return 5
    if (minutes <= 720) return 15
    if (minutes <= 1440) return 30
    return 60
}

export const bucketLabel = (minutes: number): string => {
    const size = bucketMinutes(minutes)
    return size < 60 ? `${size} min` : `${size / 60} hr`
}

export const PlayerTrend = ({
    points,
    minutes,
    live,
    className,
}: {
    /** Players in the race, one number per bucket, oldest first. */
    points: number[]
    /** How long the race runs, for the tooltip's bucket size. */
    minutes: number
    /** A running race is drawn in green, to match its state on the row. */
    live?: boolean
    className?: string
}) => {
    if (!points || points.length < 2) {
        return (
            <div className={`min-w-0 flex-1 ${className ?? ''}`} aria-hidden>
                <div className='h-8' />
            </div>
        )
    }

    const W = 240
    const H = 32
    const peak = Math.max(...points, 1)

    // Baseline at the bottom rather than at the minimum: this is a count, and a
    // line that never touches zero would make "a few players" look like none.
    const x = (i: number) => (i / (points.length - 1)) * W
    const y = (v: number) => H - (v / peak) * (H - 3) - 1.5

    const line = points.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
    const area = `0,${H} ${line} ${W},${H}`

    const stroke = live ? '#4ade80' : 'rgba(255,255,255,0.35)'
    const fill = live ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.05)'

    return (
        <div
            className={`min-w-0 flex-1 ${className ?? ''}`}
            title={`Players per ${bucketLabel(minutes)} — peak ${peak}`}
        >
            <svg
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio='none'
                className='h-8 w-full'
                role='img'
                aria-label={`Players over time, peak ${peak}`}
            >
                <polygon points={area} fill={fill} />
                <polyline
                    points={line}
                    fill='none'
                    stroke={stroke}
                    strokeWidth={1.5}
                    strokeLinejoin='round'
                    strokeLinecap='round'
                    vectorEffect='non-scaling-stroke'
                />
                {/* Where it is now. Only on a live race -- on a finished one
                    every point is "now", so a dot would say nothing. */}
                {live && (
                    <circle
                        cx={x(points.length - 1)}
                        cy={y(points[points.length - 1])}
                        r={2}
                        fill='#4ade80'
                    />
                )}
            </svg>
        </div>
    )
}
